import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useIsoLanguageCode } from '../../../utils/hooks/useIsoLanguageCode';
import { useMeQuery } from '../../../components/hooks/useMeQuery';
import { useGetAccountDetailsQuery } from '../../../queries/useGetAccountDetailsQuery';
import { useGetCustomerRiskFactors } from '../../../components/hooks/useGetCustomerRiskFactors';

import {
  getOpportunities,
  leadConvertor,
  addCRSInterestedProductFamily,
  addCRSInterestedProduct,
} from '../../../api/resilience/resilience.api';

import { buildLeadConvertorPayload } from '../utils/buildLeadConvertorPayload';
import { getPolicyNumberAndProduct } from '../utils/getPolicyNumberAndProduct';

import { SERVICE_STATUS_INFO_QUERY_KEY } from './useServiceStatusInfo';

import { IsoLanguageCodes, ProductFamilyCode } from '../types';
import { getCurrencyFromIsoLanguage } from '../utils/getCurrency';
import { useAuth } from '../../../components/hooks/useAuth';
import type { Product, Opportunity, ProductFamily } from '../types';
import type { AccountDto } from '../../../types';

type LeadConvertorMutationParams = {
  productFamily: ProductFamily;
  product?: Product;
};

// Error message constants
export const ERR_ACCOUNT_DETAILS = 'Could not find the account details';
export const ERR_USER_DETAILS = 'Could not find the user details';
export const ERR_ACCOUNT_ID = 'Could not find the account id';
export const ERR_PRODUCT_FAMILY = 'Could not find the product family details';
export const ERR_OPPORTUNITY = 'Could not find the opportunity';
export const ERR_SERVICE_UNAVAILABLE = 'Service is not available yet';
export const ERR_LEAD_CONVERSION_PERMISSION =
  "We get that you're trying to submit your service preferences, but it looks like you don't have the necessary access.";

// Custom error class for domain-specific errors
export class LeadConvertorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LeadConvertorError';
  }
}

// Helper to add interested product family
const addInterestedProductFamily = async ({
  productFamily,
  opportunityId,
  isoLanguageCode,
}: {
  productFamily: ProductFamily;
  opportunityId: string;
  isoLanguageCode: IsoLanguageCodes;
}) => {
  await addCRSInterestedProductFamily({
    data: {
      productFamilyId: productFamily.id,
      productFamilyName: productFamily.name,
      opportunityId,
      currency: getCurrencyFromIsoLanguage(isoLanguageCode),
    },
  });
};

// Helper to add interested product
const addInterestedProduct = async ({
  product,
  productFamily,
  opportunityId,
  isoLanguageCode,
  quantity,
}: {
  product: Product;
  productFamily: ProductFamily;
  opportunityId: string;
  isoLanguageCode: IsoLanguageCodes;
  quantity?: number;
}) => {
  await addCRSInterestedProduct({
    data: {
      productId: product.id,
      productName: product.name,
      opportunityId,
      currency: getCurrencyFromIsoLanguage(isoLanguageCode),
      ...(productFamily?.code === ProductFamilyCode.CTS && quantity
        ? { quantity }
        : {}),
    },
  });
};

export function useLeadConvertor(isPoolTenant = false) {
  const queryClient = useQueryClient();
  const isoLanguageCode = useIsoLanguageCode();
  const { data: me } = useMeQuery();
  const accountId = me?.account?.id;

  // For Pool tenants, we don't need account details or risk factors
  const { data: account } = useGetAccountDetailsQuery({
    accountId: isPoolTenant ? undefined : accountId,
  });
  const { query: riskFactorsQuery } = useGetCustomerRiskFactors({
    accountId: isPoolTenant ? undefined : accountId,
  });
  const { isCowbellUserJumped } = useAuth();

  const fetchOpportunities = async () => {
    const response = await getOpportunities({ params: { isoLanguageCode } });
    return response.data;
  };

  /**
   * Handles the case when there are no existing opportunities.
   */
  const handleNoOpportunities = async (
    policyHolderAccount: AccountDto,
    productFamily: ProductFamily,
    product?: Product
  ) => {
    const riskFactors = riskFactorsQuery.data;
    const policy = await getPolicyNumberAndProduct();

    const leadConvertorPayload = buildLeadConvertorPayload({
      me,
      account: policyHolderAccount,
      riskFactors,
      policy,
    });

    const { data: leadConvertorResponse } = await leadConvertor({
      data: leadConvertorPayload,
    });

    const opportunityId = leadConvertorResponse?.opportunityId;

    if (!opportunityId) {
      throw new LeadConvertorError(ERR_OPPORTUNITY);
    }

    await addInterestedProductFamily({
      productFamily,
      opportunityId,
      isoLanguageCode,
    });

    if (product) {
      await addInterestedProduct({
        product,
        productFamily,
        opportunityId,
        isoLanguageCode,
        quantity: account?.noOfEmployeesAll,
      });
    }
  };

  /**
   * Handles the case when there are existing opportunities.
   */
  const handleExistingOpportunities = async (
    opportunities: Opportunity[],
    productFamily: ProductFamily,
    product?: Product
  ) => {
    const opportunityId = opportunities[0].id;

    const isCRSProductFamilyAdded = opportunities.some((opportunity) =>
      opportunity?.productFamilies?.some((item) => item.id === productFamily.id)
    );

    if (!isCRSProductFamilyAdded) {
      await addInterestedProductFamily({
        productFamily,
        opportunityId,
        isoLanguageCode,
      });
    }

    if (product) {
      const isCRSProductAdded = opportunities.some((opportunity) =>
        opportunity.products?.some(
          (productItem) => productItem.id === product.id
        )
      );

      if (!isCRSProductAdded) {
        await addInterestedProduct({
          product,
          productFamily,
          opportunityId,
          isoLanguageCode,
          quantity: account?.noOfEmployeesAll,
        });
      }
    }
  };

  return useMutation<unknown, LeadConvertorError, LeadConvertorMutationParams>({
    mutationFn: async ({ productFamily, product }) => {
      // For Pool tenants, don't make any API calls - this is information only
      if (isPoolTenant) {
        return;
      }

      // Guard clause for required data
      if (isCowbellUserJumped) {
        return;
      }

      if (!account) {
        throw new LeadConvertorError(ERR_ACCOUNT_DETAILS);
      }

      if (!me) {
        throw new LeadConvertorError(ERR_USER_DETAILS);
      }

      if (!me.account?.id) {
        throw new LeadConvertorError(ERR_ACCOUNT_ID);
      }

      if (!productFamily) {
        throw new LeadConvertorError(ERR_PRODUCT_FAMILY);
      }

      if (productFamily.upcomingService) {
        throw new LeadConvertorError(ERR_SERVICE_UNAVAILABLE);
      }

      const opportunities = await fetchOpportunities();

      if (!Array.isArray(opportunities)) {
        throw new LeadConvertorError(ERR_OPPORTUNITY);
      }

      if (opportunities.length === 0) {
        await handleNoOpportunities(account, productFamily, product);
      } else {
        await handleExistingOpportunities(
          opportunities,
          productFamily,
          product
        );
      }

      await queryClient.invalidateQueries({
        queryKey: [SERVICE_STATUS_INFO_QUERY_KEY],
      });
    },
    onError: (error) => {
      // Only log non-sensitive error info
      if (error instanceof LeadConvertorError) {
        // eslint-disable-next-line no-console
        console.error(`[LeadConvertorError]: ${error.message}`);
      } else {
        // eslint-disable-next-line no-console
        console.error('[Unknown error in useLeadConvertor]');
      }
    },
  });
}
